import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }

    const verifyEmail = async () => {
      try {
        const res = await api.get(`/auth/verify-email/${token}`);
        setStatus("success");
        setMessage(res.data.message || "Email verified successfully!");
        // Refresh user data to update email_verified status
        await refreshUser();
      } catch (err) {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Failed to verify email. The link may be invalid or expired.");
      }
    };

    verifyEmail();
  }, [token, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-card border border-border rounded-2xl p-8 text-center shadow-xl">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg">QR</span>
            </div>
          </div>

          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="text-xl font-semibold mb-2">Verifying your email...</h1>
              <p className="text-muted-foreground text-sm">Please wait while we verify your email address.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-xl font-semibold mb-2 text-green-500">Email Verified!</h1>
              <p className="text-muted-foreground text-sm mb-6">{message}</p>
              <Button onClick={() => navigate("/dashboard")} className="w-full" data-testid="go-to-dashboard-btn">
                Go to Dashboard
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="text-xl font-semibold mb-2 text-destructive">Verification Failed</h1>
              <p className="text-muted-foreground text-sm mb-6">{message}</p>
              <div className="space-y-3">
                <Button variant="outline" onClick={() => navigate("/login")} className="w-full" data-testid="go-to-login-btn">
                  Go to Login
                </Button>
                <p className="text-xs text-muted-foreground">
                  Need a new verification link? <Link to="/login" className="text-primary hover:underline">Log in</Link> and request one from your dashboard.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
